import { z } from 'zod';
/** Mindestlänge neuer Passwörter (Beschluss Q4: 10 Zeichen). */
export const MIN_PASSWORD_LENGTH = 10;
/** Höchstlänge von Passwörtern (schützt vor überlangen Eingaben beim Hashen). */
export const MAX_PASSWORD_LENGTH = 128;
/** Rollen eines Benutzers. */
export const ROLES = ['admin', 'user'];
/** Unterstützte Oberflächensprachen. */
export const LOCALES = ['de', 'en'];
/** Benutzername: 2–64 Zeichen aus Buchstaben, Ziffern und `. _ @ -` (ohne Leerzeichen). */
export const usernameSchema = z
    .string()
    .trim()
    .min(2, 'too_short')
    .max(64, 'too_long')
    .regex(/^[\p{L}\p{N}._@-]+$/u, 'invalid_characters');
/** Neues Passwort gemäß Passwortregel (Länge). Fehlercodes sind vom Frontend übersetzbar. */
export const newPasswordSchema = z
    .string()
    .min(MIN_PASSWORD_LENGTH, 'too_short')
    .max(MAX_PASSWORD_LENGTH, 'too_long');
/** Eingabe der Anmeldung. Das Passwort wird hier nicht auf Länge geprüft (Altbestand). */
export const loginBodySchema = z.object({
    username: z.string().trim().min(1, 'required').max(64, 'too_long'),
    password: z.string().min(1, 'required').max(MAX_PASSWORD_LENGTH, 'too_long'),
});
/** Eingabe der Passwortänderung durch den angemeldeten Benutzer. */
export const changePasswordBodySchema = z.object({
    currentPassword: z.string().min(1, 'required').max(MAX_PASSWORD_LENGTH, 'too_long'),
    newPassword: newPasswordSchema,
});
/** Angemeldeter Benutzer, wie ihn die API an die Oberfläche gibt (ohne Geheimnisse). */
export const publicUserSchema = z.object({
    id: z.number().int(),
    username: z.string(),
    role: z.enum(ROLES),
    locale: z.enum(LOCALES),
    /** Ablaufdatum des Zugangs (ISO-8601) oder `null` bei unbefristeten Konten. */
    expiresAt: z.string().nullable(),
    /** `true`, wenn der Benutzer sein Passwort ändern muss, bevor er weiterarbeiten darf. */
    mustChangePassword: z.boolean(),
});
/** Antwort von Login und `GET /auth/me`. */
export const authResponseSchema = z.object({ user: publicUserSchema });
/** Zustand eines Benutzerkontos aus Sicht der Administration. */
export const ACCOUNT_STATUSES = ['active', 'expired', 'disabled'];
/** Benutzer in der Administrationsliste. */
export const adminUserSchema = publicUserSchema.extend({
    active: z.boolean(),
    status: z.enum(ACCOUNT_STATUSES),
    createdAt: z.string(),
    /** Zeitpunkt der letzten Anmeldung (ISO-8601) oder `null`. */
    lastLoginAt: z.string().nullable(),
});
/** Antwort der Benutzerliste. */
export const adminUserListSchema = z.object({ users: z.array(adminUserSchema) });
/** Antwort einer einzelnen Benutzeraktion (anlegen/ändern/sperren). */
export const adminUserResponseSchema = z.object({ user: adminUserSchema });
/** Eingabe zum Anlegen eines Benutzers durch einen Administrator. */
export const createUserBodySchema = z.object({
    username: usernameSchema,
    password: newPasswordSchema,
    role: z.enum(ROLES).default('user'),
    locale: z.enum(LOCALES).default('de'),
});
/**
 * Eingabe zum Ändern eines Benutzers. Alle Felder sind optional; ein gesetztes Passwort
 * setzt zugleich die Pflicht zur Passwortänderung beim nächsten Login.
 */
export const updateUserBodySchema = z
    .object({
    username: usernameSchema,
    password: newPasswordSchema,
    role: z.enum(ROLES),
    locale: z.enum(LOCALES),
    /** ISO-8601-Datum/Zeit oder `null` für unbefristet. */
    expiresAt: z.iso.datetime().nullable(),
})
    .partial();
/** Eintrag des Login-Protokolls. */
export const loginLogEntrySchema = z.object({
    id: z.string(),
    username: z.string(),
    loginAt: z.string(),
    logoutAt: z.string().nullable(),
    /** Dauer in Sekunden; `null`, solange die Sitzung nicht beendet wurde. */
    durationSeconds: z.number().int().nullable(),
    ip: z.string().nullable(),
});
/** Antwort des Login-Protokolls. */
export const loginLogSchema = z.object({ entries: z.array(loginLogEntrySchema) });
//# sourceMappingURL=auth.js.map